import { useRef, useState } from 'react';
import { SORTDIR, TpDataTableColumn, VALTYPES } from '../../../../data/interfaces';
import { getBoundData, getDataTable } from '../chartDataBindings';
import { TpChartProps } from '../interfaces';
import styles from './DataTable.module.scss';
import pagedTableStyles from '../../../../components/pagedTable/pagedTable.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  convertColumnDataToDisplayString,
  generateChartTitle,
  getHighlightedText,
  hasSelectedItems,
  isSelectedItem,
} from '../../helpers/misc';
import useResizeObserver from '../../hooks/resize-observer';
import SearchField from '../../../../components/search-field/SearchField';
import { getColumnInfo, getGroupInfo } from '../../../../data/analyticsDataSlice';
import { createSortIndexes } from '../../helpers/misc';
import { updateChartConfigParam, updateChartDataBinding } from '../../data/dashboardsSlice';
import { useAppDispatch } from '../../../../app/hooks';
import { sendEvent, useEventNotification } from '../../hooks/events';
import { MSGDataContextSelectionChanged } from '../../../../data/selection/selectionEnFilters';
import { color } from '../../helpers/colors';
import { useColumnResize } from '../../../../components/hooks/useColumnResize';
import { TpColumnConfig } from './interfaces';
import { getCellStyle, getColumnWidth, isUtilityColumn } from './cellUtils';
import { ActionTableCell } from './ActionTableCell';

interface TpTableWidgetColumn {
  colInfo: TpDataTableColumn;
  width: number;
  config: TpColumnConfig;
}

export function DataTable({ chartTypeManifest, dataContext, dataBinding, config, widgetContext }: TpChartProps) {
  const dispatch = useAppDispatch();

  const [searchString, setSearchString] = useState('');

  const [pageRowCount, setPageRowCount] = useState(0);
  const [pageOffset, setPageOffset] = useState(0);

  useEventNotification(
    MSGDataContextSelectionChanged,
    (tpe: string, contentInput: any) => {
      // In case
      //   - the current selection filter has been changed for the data shown in this table,
      //   - and we only show the selection,
      //  we should reset the pager to the top, as the content of the table has been modified
      if (
        !config.includeUnselected &&
        contentInput.dataContextId == dataContext.id &&
        contentInput.canonicalTypeId == getDataTable(dataContext, dataBinding.dataTableId).canonicalTypeId
      )
        setPageOffset(0);
    },
    [config], // execution of this effect depends on the config values!
  );

  const refTableWrapper = useRef<HTMLDivElement>(null);

  const boundData = getBoundData(chartTypeManifest, dataContext, dataBinding);
  const dataTable = boundData.dataTable;

  const selectedItemsMap = boundData.canonicalTypeInfo.selectedItemsMap;
  const restrictedItemsMap = boundData.canonicalTypeInfo.restrictedItemsMap;
  const isRestricted = !!restrictedItemsMap;
  const hasSelection = hasSelectedItems(selectedItemsMap);

  const dataItemId = dataTable.columnData[dataTable.colItemId];
  const sortByColId = boundData.bindings.sortBy?.columnInfo.id;
  const sortInv = config.sortDir == SORTDIR.DESC;
  const sortIndex = createSortIndexes(
    // we create sort indexes for the full dataset, without any potential restriction applied
    dataItemId.length,
    sortInv,
    boundData.bindings.sortBy?.columnInfo,
    dataTable.columnData[sortByColId],
  )[0];

  const downloadTable = () => {
    if (isRestricted && !hasSelection) {
      sendEvent('RestrictedDownload', { assetId: dataContext.assetId, canonicalTypeId: dataTable.canonicalTypeId });
    } else if (hasSelection) {
      sendEvent('PartialDownload', { assetId: dataContext.assetId, canonicalTypeId: dataTable.canonicalTypeId });
    } else {
      sendEvent('Download', { assetId: dataContext.assetId, canonicalTypeId: dataTable.canonicalTypeId });
    }
  };

  function getHighlightedBadge(
    text: string,
    highlight: string,
    categoryColors: { content: string; backgroundColor: { r: number; g: number; b: number } }[] | null,
  ): any {
    const isHighlighted = highlight && text.toLowerCase().includes(highlight.toLowerCase());

    const style: any = {};
    if (categoryColors) {
      const category = categoryColors.find((cat) => cat.content == text);
      if (category)
        style.backgroundColor = color(
          category.backgroundColor.r,
          category.backgroundColor.g,
          category.backgroundColor.b,
        ).toCanvasString();
    }

    return (
      <span key={text} className={isHighlighted ? styles.badgeHighlighted : styles.badge} style={style}>
        {text}
      </span>
    );
  }

  if (widgetContext.setBitmapDownloadFunction) widgetContext.setBitmapDownloadFunction(downloadTable);

  useResizeObserver(refTableWrapper, (info: any) => {
    if (!refTableWrapper.current) return;
    const style = getComputedStyle(refTableWrapper.current);
    const clientHeight = refTableWrapper.current.clientHeight;
    const secondHeaderHeight = config.showGroupHeaders ? 60 : 30;
    const rowCount = Math.floor((clientHeight - secondHeaderHeight) / 28);
    setPageRowCount(rowCount);
  });

  function toggleSelection(itemId: string) {
    widgetContext.toggleSingleItemSelection(itemId);
  }

  function setSortColumn(colId: string) {
    if (colId != sortByColId) {
      dispatch(
        updateChartDataBinding({
          dashboardDefId: widgetContext.dashboardContext.dashboardDefId,
          chartId: widgetContext.widgetId,
          aspectId: 'sortBy',
          columnId: colId,
        }),
      );
      if (sortInv)
        dispatch(
          updateChartConfigParam({
            dashboardDefId: widgetContext.dashboardContext.dashboardDefId,
            chartId: widgetContext.widgetId,
            configParamId: 'sortDir',
            value: SORTDIR.ASC,
          }),
        );
    } else {
      dispatch(
        updateChartConfigParam({
          dashboardDefId: widgetContext.dashboardContext.dashboardDefId,
          chartId: widgetContext.widgetId,
          configParamId: 'sortDir',
          value: sortInv ? SORTDIR.ASC : SORTDIR.DESC,
        }),
      );
    }
    setPageOffset(0);
  }

  const usedColumns: TpTableWidgetColumn[] = [];
  for (const colDef of config.columns) {
    usedColumns.push({
      colInfo: getColumnInfo(dataTable, colDef.id),
      width: getColumnWidth(colDef),
      config: colDef,
    });
  }
  const { columnWidths, totalTableWidth, onPointerDownResize } = useColumnResize({
    initialColumns: usedColumns.map((c) => ({ id: c.colInfo.id, width: c.width })),
  });

  let totalWidth = totalTableWidth;
  let rows = [];

  const headerGroups: { name: string; startSpan: number; endSpan: number; width: number }[] = [];
  let currentGroupId = null;
  for (const [index, col] of usedColumns.entries()) {
    if ((col.colInfo.groupId || '---') !== currentGroupId) {
      currentGroupId = col.colInfo.groupId || '---';
      headerGroups.push({
        name: col.colInfo.groupId ? getGroupInfo(dataTable, col.colInfo.groupId).name : '',
        startSpan: index,
        endSpan: index,
        width: 0,
      });
    }
    if (headerGroups.length > 0) {
      headerGroups[headerGroups.length - 1].endSpan = index;
      headerGroups[headerGroups.length - 1].width += columnWidths[index];
    }
  }
  let headersLevel2 = headerGroups.map((group) => (
    //we need to make sure to not forget the padding between the columns
    <th key={`key_${group.startSpan}`} style={{ width: `${group.width + (group.endSpan - group.startSpan) * 8}px` }}>
      {group.name && (
        <div className={styles.level2HeaderSpanIndicator} title={group.name}>
          {group.name}
        </div>
      )}
    </th>
  ));

  let headers = usedColumns.map((column: TpTableWidgetColumn, index: number) => {
    if (isUtilityColumn(column.config)) {
      return (
        <th key={column.colInfo.id} style={{ width: `${column.width}px` }}>
          {/* Empty header for utility column */}
        </th>
      );
    }
    return (
      <th key={column.colInfo.id} style={{ width: `${columnWidths[index]}px` }}>
        <span className={pagedTableStyles.columnResizer} onPointerDown={(e) => onPointerDownResize(e, index)}></span>
        <button
          className={column.colInfo.id == sortByColId ? styles.columnSortButtonActive : styles.columnSortButton}
          onClick={() => setSortColumn(column.colInfo.id)}
        >
          {column.colInfo.id == sortByColId && sortInv ? (
            <FontAwesomeIcon icon="caret-down" />
          ) : (
            <FontAwesomeIcon icon="caret-up" />
          )}
        </button>
        <i title={column.colInfo.name}>{column.colInfo.name}</i>
      </th>
    );
  });
  totalWidth += 25;
  headersLevel2 = [<th key="selected" style={{ width: '25px' }}></th>, ...headersLevel2];
  headers = [<th key="selected" style={{ width: '25px' }}></th>, ...headers];

  let displayedCount = 0;
  let endOfPageOffset = 0;

  let selectedCount = 0;
  for (let idx = 0; idx < dataItemId.length; idx++) {
    const idxSorted = sortIndex[idx];
    const itemId = dataTable.columnData[dataTable.colItemId][idxSorted];
    if (!isRestricted || restrictedItemsMap[itemId]) {
      if (config.includeUnselected || isSelectedItem(selectedItemsMap, itemId)) {
        selectedCount += 1;
        let inMatch = true;
        if (searchString) {
          inMatch = false;
          for (const col of usedColumns) {
            if (
              col.colInfo.valueType == VALTYPES.CATEGORICAL ||
              col.colInfo.valueType == VALTYPES.STRING ||
              col.colInfo.valueType == VALTYPES.MULTICAT
            ) {
              const content = convertColumnDataToDisplayString(
                col.colInfo,
                dataTable.columnData[col.colInfo.id][idxSorted],
              );
              if (content && String(content).toLowerCase().indexOf(searchString.toLowerCase()) >= 0) inMatch = true;
            }
          }
        }
        if (inMatch) {
          if (displayedCount >= pageOffset && displayedCount < pageOffset + pageRowCount) {
            endOfPageOffset = displayedCount;
            const itemColData = [];
            let columns = usedColumns.map((column: TpTableWidgetColumn, index: number) => {
              const columnConfig = config.columns[index];
              const cellValue = dataTable.columnData[column.colInfo.id][idxSorted];
              const displayString = convertColumnDataToDisplayString(column.colInfo, cellValue);

              if (columnConfig.action) {
                return (
                  <ActionTableCell
                    key={column.colInfo.id}
                    columnConfig={columnConfig}
                    colInfo={column.colInfo}
                    dataTable={dataTable}
                    rowIndex={idxSorted}
                    searchString={searchString}
                  />
                );
              }
              if (columnConfig.useBadge) {
                if (displayString === '' || displayString === null || displayString === undefined) {
                  return <td key={column.colInfo.id}></td>;
                }
                return (
                  <td key={column.colInfo.id} style={getCellStyle(columnConfig)}>
                    {displayString
                      .split('; ')
                      .map((value: string, idx: number) =>
                        getHighlightedBadge(value, searchString, columnConfig.categoryColors),
                      )}
                  </td>
                );
              }
              if (columnConfig.iconMapper && Object.keys(columnConfig.iconMapper).length > 0) {
                if (displayString in columnConfig.iconMapper) {
                  return (
                    <td key={column.colInfo.id} className={styles.iconCell} style={getCellStyle(columnConfig)}>
                      <FontAwesomeIcon icon={columnConfig.iconMapper[displayString]} />
                    </td>
                  );
                } else {
                  return (
                    <td key={column.colInfo.id} style={getCellStyle(columnConfig)}>
                      {getHighlightedText(convertColumnDataToDisplayString(column.colInfo, cellValue), searchString)}
                    </td>
                  );
                }
              } else {
                let numericalBar = null;
                if (columnConfig.numericalBar) {
                  const fracWidth = Math.max(
                    0,
                    Math.min(
                      1,
                      (cellValue - columnConfig.numericalBar.minVal) /
                        (columnConfig.numericalBar.maxVal - columnConfig.numericalBar.minVal),
                    ),
                  );
                  numericalBar = (
                    <div className={styles.fracBar} style={{ width: `${fracWidth * columnWidths[index]}px` }} />
                  );
                }
                return (
                  <td key={column.colInfo.id} style={getCellStyle(columnConfig)}>
                    {numericalBar}
                    {getHighlightedText(convertColumnDataToDisplayString(column.colInfo, cellValue), searchString)}
                  </td>
                );
              }
            });
            let selIcon = (
              <span style={{ color: 'var(--base-color)', opacity: 0.5, display: 'inline-block', paddingTop: '3px' }}>
                <FontAwesomeIcon icon={['fas', 'circle']} />
              </span>
            );
            if (hasSelection) {
              selIcon = (
                <span style={{ opacity: 0.1, display: 'inline-block', paddingTop: '3px' }}>
                  <FontAwesomeIcon icon={['fas', 'circle']} />
                </span>
              );
              if (isSelectedItem(selectedItemsMap, itemId))
                selIcon = (
                  <span style={{ color: 'var(--base-color)', display: 'inline-block', paddingTop: '3px' }}>
                    <FontAwesomeIcon icon={['fas', 'circle-check']} />
                  </span>
                );
            }
            columns = [
              <td
                key="sel"
                onClick={(ev) => {
                  toggleSelection(itemId);
                  ev.stopPropagation();
                }}
              >
                {selIcon}
              </td>,
              ...columns,
            ];
            rows.push({
              itemId,
              columns,
            });
          }
          displayedCount++;
        }
      }
    }
  }

  rows = rows.map((row: any, rowIdx: number) => (
    <tr
      key={`rw_${rowIdx}`}
      onClick={() => {
        if (row.itemId) widgetContext.handleOpenItem(dataTable.canonicalTypeId, row.itemId);
      }}
    >
      {row.columns}
    </tr>
  ));

  const title = generateChartTitle(dataContext, dataBinding, config, dataTable.name);

  return (
    <>
      <div className={styles.header}>
        {title} ({selectedCount})
        {hasSelection && (
          <div
            className={styles.filterToggleSection}
            onClick={() => {
              dispatch(
                updateChartConfigParam({
                  dashboardDefId: widgetContext.dashboardContext.dashboardDefId,
                  chartId: widgetContext.widgetId,
                  configParamId: 'includeUnselected',
                  value: !config.includeUnselected,
                }),
              );
            }}
          >
            <FontAwesomeIcon icon="filter" />
            <br />
            <span>
              {config.includeUnselected ? <FontAwesomeIcon icon="toggle-off" /> : <FontAwesomeIcon icon="toggle-on" />}
            </span>
          </div>
        )}
      </div>
      <div className={styles.tableWrapper} ref={refTableWrapper}>
        {/* We need to split it up as 2 tables otherwise the Colspan messes up the original headers */}
        {config.showGroupHeaders && (
          <table style={{ width: `${totalWidth}px` }}>
            <thead>
              <tr>{headersLevel2}</tr>
            </thead>
          </table>
        )}
        <table style={{ width: `${totalWidth}px` }}>
          <thead>
            <tr>{headers}</tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </div>
      <div className={styles.footer}>
        <div style={{ display: 'inline-block', width: '140px' }}>
          <SearchField
            updateSearchString={(newVal: string) => {
              setSearchString(newVal);
              setPageOffset(0);
            }}
          />
        </div>
        <div className={styles.pager}>
          {pageOffset > 0 && (
            <button onClick={() => setPageOffset(Math.max(0, pageOffset - pageRowCount))}>
              <FontAwesomeIcon icon="caret-left" />
            </button>
          )}
          {pageOffset + 1} - {endOfPageOffset + 1} / {displayedCount}
          {endOfPageOffset + 1 < displayedCount && (
            <button onClick={() => setPageOffset(pageOffset + pageRowCount)}>
              <FontAwesomeIcon icon="caret-right" />
            </button>
          )}
        </div>
      </div>
    </>
  );
}
